import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import api            from '../../api/client';
import { useAuth }   from '../../contexts/AuthContext';
import StatCard       from '../../components/StatCard';
import GaugeCard      from '../../components/GaugeCard';
import HeatmapChart   from '../../components/HeatmapChart';
import AlertsPanel    from '../../components/AlertsPanel';
import DeviceStatusTable from '../../components/DeviceStatusTable';

/* ── yardımcılar ── */
const fmt = n => Number(n || 0).toLocaleString('tr-TR');

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border2)', borderRadius:8, padding:'10px 14px', fontSize:12 }}>
      <div style={{ color:'var(--text2)', marginBottom:4 }}>{label}</div>
      {payload.map(p => <div key={p.dataKey} style={{ color:p.color }}>{p.name}: <strong>{Number(p.value).toLocaleString('tr-TR')}</strong></div>)}
    </div>
  );
};

const DENSITY_CFG = {
  low:      { color:'var(--green)',   bg:'var(--green-dim)',   label:'Düşük'  },
  normal:   { color:'var(--accent2)', bg:'var(--accent-dim)',  label:'Normal' },
  high:     { color:'var(--yellow)',  bg:'var(--yellow-dim)',  label:'Yoğun'  },
  critical: { color:'var(--red)',     bg:'var(--red-dim)',     label:'Kritik' },
};

const DAY_NAMES = ['','Paz','Pzt','Sal','Çar','Per','Cum','Cmt'];

/* Bölüm başlığı */
const Sec = ({ title, sub, children, action }) => (
  <div style={{ marginBottom:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <div>
        <h2 style={{ fontSize:15, fontWeight:500 }}>{title}</h2>
        {sub && <p style={{ fontSize:12, color:'var(--text3)', marginTop:2 }}>{sub}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

/* Karşılaştırma satırı */
const CRow = ({ label, val, sub }) => (
  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
    <span style={{ fontSize:sub?11:13, color:sub?'var(--text3)':'var(--text2)' }}>{label}</span>
    <strong style={{ fontSize:sub?11:13 }}>{fmt(val)}</strong>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Mall / AVM Dashboard
   ═══════════════════════════════════════════════════════════════════════════ */
export default function CustomerDashboard() {
  const { user }  = useAuth();
  const isStore   = user?.dashboard_mode === 'store_group';

  if (isStore) {
    // eslint-disable-next-line global-require
    const StoreGroupDashboard = require('./StoreGroupDashboard').default;
    return <StoreGroupDashboard />;
  }

  return <MallDashboard />;
}

function MallDashboard() {
  const navigate = useNavigate();

  /* ── Orijinal state — hiçbiri kaldırılmadı ── */
  const [devices,      setDevices]      = useState([]);
  const [selDevice,    setSelDevice]    = useState('');
  const [rt,           setRt]           = useState(null);
  const [summ,         setSumm]         = useState(null);
  const [hourlyData,   setHourlyData]   = useState([]);
  const [dailyData,    setDailyData]    = useState([]);
  const [heatmap,      setHeatmap]      = useState([]);
  const [weekdays,     setWeekdays]     = useState([]);
  const [hourAnalysis, setHourAnalysis] = useState(null);
  const [occupancy,    setOccupancy]    = useState(null);
  const [records,      setRecords]      = useState(null);
  const [cmpWeek,      setCmpWeek]      = useState(null);
  const [cmpMonth,     setCmpMonth]     = useState(null);
  const [alarms,       setAlarms]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const rtRef = useRef(null);

  const dParam = selDevice ? { device_id: selDevice } : {};

  /* Cihaz listesi */
  useEffect(() => {
    api.get('/reports/devices-summary').then(r => setDevices(r.data.data || [])).catch(() => {});
  }, []);

  /* Statik veri — orijinal 11 API çağrısı korunuyor */
  const loadStatic = useCallback(async () => {
    setLoading(true);
    try {
      const p = dParam;
      const [s, h, d, hm, wd, ha, oc, rec, cw, cm, al] = await Promise.all([
        api.get('/reports/summary',             { params: p }),
        api.get('/reports/hourly',              { params: p }),
        api.get('/reports/daily',               { params: { ...p, days:30 } }),
        api.get('/reports/heatmap',             { params: { ...p, days:14 } }),
        api.get('/reports/weekdays',            { params: { weeks:12 } }),
        api.get('/reports/hour-analysis',       { params: { days:30 } }),
        api.get('/reports/occupancy',           { params: p }),
        api.get('/reports/records'),
        api.get('/reports/comparison/week'),
        api.get('/reports/comparison/month'),
        api.get('/reports/alerts'),
      ]);
      setSumm(s.data.data);
      setHourlyData(h.data.data?.data || []);
      setDailyData((d.data.data || []).map(r => ({ ...r, date: String(r.date).split('T')[0], total_in: Number(r.total_in) })));
      setHeatmap(hm.data.data || []);
      setWeekdays(wd.data.data?.data || []);
      setHourAnalysis(ha.data.data);
      setOccupancy(oc.data.data);
      setRecords(rec.data.data);
      setCmpWeek(cw.data.data);
      setCmpMonth(cm.data.data);
      setAlarms(al.data.data?.alarms || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selDevice]); // eslint-disable-line

  useEffect(() => { loadStatic(); }, [loadStatic]);

  /* Realtime 15 sn — orijinal mantık korunuyor */
  const loadRt = useCallback(() => {
    api.get('/reports/realtime', { params: dParam }).then(r => {
      setRt(r.data.data);
      if (r.data.data?.devices) setDevices(r.data.data.devices);
    }).catch(() => {});
  }, [selDevice]); // eslint-disable-line

  useEffect(() => {
    loadRt();
    rtRef.current = setInterval(loadRt, 15000);
    return () => clearInterval(rtRef.current);
  }, [loadRt]);

  const onlineDevices  = devices.filter(d => d.status === 'online');
  const offlineDevices = devices.filter(d => d.status === 'offline');
  const sortedDevices  = [...devices].sort((a, b) => Number(b.today_in) - Number(a.today_in));

  /* ════════════════════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════════════════════ */
  return (
    <div>
      {/* ── Başlık + cihaz seçici (orijinal korundu) ── */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:18, fontWeight:500, marginBottom:3 }}>AVM Ziyaretçi Analitik</h1>
          <p style={{ fontSize:12, color:'var(--text3)' }}>
            <span style={{ color:'var(--green)', fontWeight:600 }}>{onlineDevices.length} online</span>
            {offlineDevices.length > 0 && <span style={{ color:'var(--red)', marginLeft:10, fontWeight:600 }}>{offlineDevices.length} offline</span>}
            <span style={{ color:'var(--text3)', marginLeft:10 }}>• {devices.length} cihaz</span>
          </p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <select value={selDevice} onChange={e => setSelDevice(e.target.value)} style={{ width:200 }}>
            <option value="">Tüm Cihazlar</option>
            {devices.map(d => <option key={d.id} value={d.id}>{d.status==='online'?'● ':'○ '}{d.name}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={loadStatic} title="Yenile">↻</button>
        </div>
      </div>

      {/* ── Alarmlar (orijinal AlertsPanel) ── */}
      {alarms.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <AlertsPanel alarms={alarms} compact />
        </div>
      )}

      {/* ══ İki kolon layout ══ */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:16, alignItems:'start' }}>

        {/* ─── Sol: Ana içerik ─── */}
        <div>

          {/* Anlık 4+2 kartlar */}
          <Sec title="Anlık Durum" sub="15 saniyede bir güncellenir">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, minmax(0,1fr))', gap:10, marginBottom:10 }}>
              <StatCard label="İçerideki Kişi" value={rt ? fmt(rt.inside_count) : '—'} icon="🏢" color="var(--accent)" size="lg"
                sub={rt ? `Bugün: ${fmt(rt.today_in)} giriş` : ''} />
              <StatCard label="Son 5 Dakika"  value={rt ? fmt(rt.last_5min)  : '—'} icon="⚡" color="var(--green)" />
              <StatCard label="Son 15 Dakika" value={rt ? fmt(rt.last_15min) : '—'} icon="🕒" color="var(--purple)" />
              <StatCard label="Son 1 Saat"    value={rt ? fmt(rt.last_60min) : '—'} icon="⏱️" color="var(--yellow)" />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div className="card" style={{ padding:'14px 16px' }}>
                <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)', marginBottom:8 }}>Yoğunluk</div>
                {rt ? (
                  <>
                    <span style={{ background:DENSITY_CFG[rt.density]?.bg, color:DENSITY_CFG[rt.density]?.color, padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600 }}>
                      ● {DENSITY_CFG[rt.density]?.label}
                    </span>
                    <div style={{ fontSize:11, color:'var(--text3)', marginTop:8 }}>
                      {rt.last_event_time ? `Son: ${new Date(rt.last_event_time).toLocaleTimeString('tr-TR')}` : 'Veri yok'}
                    </div>
                  </>
                ) : <div style={{ color:'var(--text3)', fontSize:12 }}>Bekleniyor...</div>}
              </div>
              <div className="card" style={{ padding:'14px 16px' }}>
                <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)', marginBottom:8 }}>Cihazlar</div>
                <div style={{ display:'flex', gap:16 }}>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:22, fontFamily:'var(--font-display)', fontWeight:700, color:'var(--green)' }}>{onlineDevices.length}</div>
                    <div style={{ fontSize:10, color:'var(--text3)' }}>Online</div>
                  </div>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:22, fontFamily:'var(--font-display)', fontWeight:700, color:'var(--red)' }}>{offlineDevices.length}</div>
                    <div style={{ fontSize:10, color:'var(--text3)' }}>Offline</div>
                  </div>
                </div>
              </div>
            </div>
          </Sec>

          {/* Dönemsel 6 StatCard */}
          <Sec title="Dönemsel Özet">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(6, minmax(0,1fr))', gap:10 }}>
              <StatCard label="Bugün"    value={fmt(summ?.today)}      icon="📅" color="var(--accent)"  size="lg" trend={summ?.change_today_pct} trendLabel="dünden"        loading={loading} />
              <StatCard label="Dün"      value={fmt(summ?.yesterday)}  icon="📆" color="var(--purple)" loading={loading} />
              <StatCard label="Bu Hafta" value={fmt(summ?.this_week)}  icon="📊" color="var(--green)"  trend={summ?.change_week_pct}  trendLabel="geçen haftadan" loading={loading} />
              <StatCard label="Geç. Hafta" value={fmt(summ?.prev_week)} icon="📉" color="var(--text3)" loading={loading} />
              <StatCard label="Bu Ay"   value={fmt(summ?.this_month)} icon="🗓️" color="var(--yellow)" trend={summ?.change_month_pct} trendLabel="geçen aydan"   loading={loading} />
              <StatCard label="Bu Yıl"  value={fmt(summ?.this_year)}  icon="🏆" color="var(--red)"    loading={loading} />
            </div>
          </Sec>

          {/* Grafikler 2 kolon */}
          <Sec title="Grafikler">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div className="card" style={{ padding:'16px 18px' }}>
                <h3 style={{ fontSize:14, marginBottom:14 }}>Bugünkü Saatlik Dağılım</h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={hourlyData} margin={{ top:0, right:8, bottom:0, left:-18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="hour" tick={{ fill:'var(--text3)', fontSize:10 }} tickFormatter={h=>`${h}`} />
                    <YAxis tick={{ fill:'var(--text3)', fontSize:10 }} />
                    <Tooltip content={<Tip />} labelFormatter={l=>`${l}:00`} />
                    <Bar dataKey="total_in" fill="var(--accent)" radius={[3,3,0,0]} name="Giriş" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="card" style={{ padding:'16px 18px' }}>
                <h3 style={{ fontSize:14, marginBottom:14 }}>Son 30 Gün Trendi</h3>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={dailyData} margin={{ top:0, right:8, bottom:0, left:-18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="date" tick={{ fill:'var(--text3)', fontSize:9 }} tickFormatter={v=>v?.slice(5)} />
                    <YAxis tick={{ fill:'var(--text3)', fontSize:10 }} />
                    <Tooltip content={<Tip />} />
                    <Line type="monotone" dataKey="total_in" stroke="var(--accent)" strokeWidth={2.5} dot={false} name="Giriş" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Sec>

          {/* Isı haritası */}
          <Sec title="Isı Haritası — Son 14 Gün">
            <div className="card" style={{ padding:'16px 18px' }}>
              <HeatmapChart data={heatmap} />
            </div>
          </Sec>

          {/* Haftanın günleri + Saat analizi */}
          <Sec title="Zaman Analizi">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div className="card" style={{ padding:'16px 18px' }}>
                <h3 style={{ fontSize:14, marginBottom:14 }}>Haftanın Günleri Analizi</h3>
                {weekdays.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart data={[...weekdays].sort((a,b)=>a.dow-b.dow)} margin={{ top:0, right:5, bottom:0, left:-20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis dataKey="dow" tick={{ fill:'var(--text3)', fontSize:10 }} tickFormatter={d=>DAY_NAMES[d]||d} />
                        <YAxis tick={{ fill:'var(--text3)', fontSize:10 }} />
                        <Tooltip content={<Tip />} labelFormatter={d=>DAY_NAMES[d]||d} />
                        <Bar dataKey="avg_in" fill="var(--purple)" radius={[3,3,0,0]} name="Ort. Giriş" />
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{ marginTop:10 }}>
                      {weekdays.slice(0,3).map((d,i) => (
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                          <span style={{ color:'var(--text2)' }}>{i+1}. {DAY_NAMES[d.dow]}</span>
                          <strong>{fmt(d.avg_in)}</strong>
                        </div>
                      ))}
                    </div>
                  </>
                ) : <div style={{ color:'var(--text3)', fontSize:12 }}>Yeterli veri yok</div>}
              </div>

              <div className="card" style={{ padding:'16px 18px' }}>
                <h3 style={{ fontSize:14, marginBottom:14 }}>Saat Analizi</h3>
                {hourAnalysis ? (
                  <>
                    <div style={{ marginBottom:12 }}>
                      <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>En Yoğun Saatler</div>
                      {(hourAnalysis.busiest||[]).slice(0,5).map((h,i)=>(
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                          <span style={{ color:'var(--text2)' }}>{i+1}. {String(h||'—')}</span>
                          <strong style={{ color:'var(--accent2)' }}>{fmt(hourAnalysis.data?.find(r=>r.hour===h)?.avg_in||0)}</strong>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>En Sakin Saatler</div>
                      {(hourAnalysis.quietest||[]).slice(0,3).map((h,i)=>(
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                          <span style={{ color:'var(--text2)' }}>{i+1}. {String(h||'—')}</span>
                          <strong style={{ color:'var(--text3)' }}>{fmt(hourAnalysis.data?.find(r=>r.hour===h)?.avg_in||0)}</strong>
                        </div>
                      ))}
                    </div>
                  </>
                ) : <div style={{ color:'var(--text3)', fontSize:12 }}>Yeterli veri yok</div>}
              </div>
            </div>
          </Sec>

          {/* Rekorlar */}
          {records && (
            <Sec title="Zirve Rekorları">
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12 }}>
                {[
                  { label:'Günlük Rekor',   value:records.daily?.value,   period:records.daily?.date },
                  { label:'Haftalık Rekor', value:records.weekly?.value,  period:records.weekly?.period },
                  { label:'Aylık Rekor',    value:records.monthly?.value, period:records.monthly?.period },
                  { label:'Yıllık Rekor',   value:records.yearly?.value,  period:String(records.yearly?.year||'') },
                ].map(r => (
                  <div key={r.label} style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'14px 16px' }}>
                    <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)', marginBottom:6 }}>{r.label}</div>
                    <div style={{ fontSize:22, fontFamily:'var(--font-display)', fontWeight:700, color:'var(--yellow)' }}>{fmt(r.value)}</div>
                    {r.period && <div style={{ fontSize:10, color:'var(--text3)', marginTop:4 }}>{r.period}</div>}
                  </div>
                ))}
              </div>
            </Sec>
          )}

          {/* Cihaz durum tablosu */}
          <div className="card" style={{ padding:'16px 18px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <h3 style={{ fontSize:14 }}>Cihaz Durumları</h3>
              <span style={{ fontSize:12, color:'var(--text3)' }}>30 sn otomatik güncelleme</span>
            </div>
            <DeviceStatusTable devices={devices} />
          </div>

        </div>

        {/* ─── Sağ: Sticky panel ─── */}
        <div style={{ display:'flex', flexDirection:'column', gap:14, position:'sticky', top:16 }}>

          {/* Doluluk gauge */}
          {occupancy && <GaugeCard value={occupancy.occupancy_pct} sub={`${fmt(occupancy.inside)} / ${fmt(occupancy.capacity)}`} label="AVM Doluluk" />}

          {/* Haftalık karşılaştırma */}
          <div className="card" style={{ padding:'16px 18px' }}>
            <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)', marginBottom:12 }}>Haftalık Karşılaştırma</div>
            <CRow label="Bu Hafta"    val={cmpWeek?.this_week} />
            <CRow label="Geçen Hafta" val={cmpWeek?.last_week} />
            {typeof cmpWeek?.change_pct === 'number' && (
              <div style={{ marginTop:8, fontSize:20, fontFamily:'var(--font-display)', fontWeight:700, color: cmpWeek.change_pct>=0?'var(--green)':'var(--red)' }}>
                {cmpWeek.change_pct>=0?'+':''}{cmpWeek.change_pct}%
              </div>
            )}
          </div>

          {/* Aylık karşılaştırma */}
          <div className="card" style={{ padding:'16px 18px' }}>
            <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)', marginBottom:12 }}>Aylık Karşılaştırma</div>
            <CRow label="Bu Ay"       val={cmpMonth?.this_month} />
            <CRow label="Geçen Ay"    val={cmpMonth?.last_month} />
            <CRow label="Geçen Yıl"   val={cmpMonth?.same_month_last_year} sub />
            {typeof cmpMonth?.change_vs_last_month === 'number' && (
              <div style={{ marginTop:6, fontSize:18, fontFamily:'var(--font-display)', fontWeight:700, color: cmpMonth.change_vs_last_month>=0?'var(--green)':'var(--red)' }}>
                {cmpMonth.change_vs_last_month>=0?'+':''}{cmpMonth.change_vs_last_month}%
              </div>
            )}
          </div>

          {/* Kapı performansı */}
          {sortedDevices.length > 0 && (
            <div className="card" style={{ padding:'16px 18px' }}>
              <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)', marginBottom:12 }}>Kapı Performansı — Bugün</div>
              {sortedDevices.map((d, i) => {
                const maxIn = Number(sortedDevices[0]?.today_in) || 1;
                const pct   = (Number(d.today_in) / maxIn) * 100;
                const isTop = i === 0;
                return (
                  <div key={d.id} style={{ marginBottom:8 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:12 }}>
                      <span style={{ color:isTop?'var(--green)':'var(--text2)', fontWeight:isTop?600:400 }}>
                        {d.status==='online' ? '● ' : '○ '}{d.name}
                      </span>
                      <strong style={{ color:isTop?'var(--green)':'var(--text)' }}>{d.status==='online'?fmt(d.today_in):'—'}</strong>
                    </div>
                    <div style={{ height:4, background:'var(--border2)', borderRadius:2 }}>
                      {d.status==='online' && <div style={{ height:4, width:`${pct}%`, background:isTop?'var(--green)':'var(--accent)', borderRadius:2 }} />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Hızlı erişim */}
          <div className="card" style={{ padding:'16px 18px' }}>
            <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text3)', marginBottom:10 }}>Hızlı Erişim</div>
            {[
              { icon:'ti-scale',          label:'Kapı karşılaştır',      path:'/dashboard/reports?module=cmpdev'  },
              { icon:'ti-calendar-stats', label:'Tarih aralığı raporu',  path:'/dashboard/reports?module=range'   },
              { icon:'ti-bell',           label:'Alarmları gör',          path:'/dashboard/alarms'  },
              { icon:'ti-download',       label:'Excel / PDF indir',      path:'/dashboard/reports?module=export'  },
            ].map(item => (
              <button key={item.label} onClick={() => navigate(item.path)} className="quick-btn">
                <i className={`ti ${item.icon}`} style={{ fontSize:14, color:'var(--accent2)' }} aria-hidden="true" />
                {item.label}
                <i className="ti ti-arrow-right" style={{ marginLeft:'auto', fontSize:12, opacity:.5 }} aria-hidden="true" />
              </button>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
